import React, { useState, useEffect, useRef } from "react";
import "../css/Common.css";
import "./About.css";
import { Link } from "react-router-dom";
import { APP_NAME } from "../Utils/constant";
import TestimonialSection from "../components/Testimonial";
import ChatButton from "../components/ChatButton";
import AboutBannerImage from "../images/AboutBanner.jpg";

const About = () => {
  const [activePhone, setActivePhone] = useState("center");
  const [phoneData, setPhoneData] = useState({
    center: {
      type: "dashboard",
      app: "Udhaar Portal",
      amount: "₹1,00,000",
      title: "Loan Limit",
      status: "READY",
      details: ["Interest: 12% p.a.", "Tenure: 12 months"],
      buttons: [
        { icon: "⚡", text: "Instant Apply" },
        { icon: "📱", text: "Track Loan" },
      ],
    },
    personal: {
      type: "personal",
      app: "Udhaar Portal",
      amount: "₹2L",
      title: "PERSONAL LOAN",
      status: "AVAILABLE",
      details: ["No Collateral", "Quick Approval"],
      buttons: [{ icon: "₹", text: "Apply Now" }],
    },
    emergency: {
      type: "emergency",
      app: "Cash",
      amount: "₹1L",
      title: "EMERGENCY LOAN",
      status: "READY",
      details: ["24/7 Available", "Same Day Disbursal"],
      buttons: [{ icon: "🚀", text: "Get Started" }],
    },
  });

  const handlePhoneClick = (phoneType) => {
    console.log("Phone clicked:", phoneType, "Current active:", activePhone);
    if (phoneType !== activePhone) {
      setActivePhone(phoneType);
    }
  };

  const getCurrentPhoneData = () => {
    return phoneData[activePhone] || phoneData.center;
  };

  // Animation states
  const [isVisible, setIsVisible] = useState({});
  const [counters, setCounters] = useState({
    customers: 0,
    disbursed: 0,
    approval: 0,
    satisfaction: 0,
  });
  const [typingText, setTypingText] = useState("");
  const [typingIndex, setTypingIndex] = useState(0);

  // Refs for scroll detection
  const sectionRefs = useRef({});
  const observerRef = useRef(null);

  // Typing animation text
  const typingTexts = [
    "POWERING INSTANT LOANS",
    "WITH SCALABLE FINANCIAL SOLUTIONS",
    "Your Financial Freedom Starts Here",
  ];

  // Scroll-triggered animation observer
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    // Observe all sections
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observerRef.current.observe(ref);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Counter animation
  useEffect(() => {
    const animateCounters = () => {
      const targets = {
        customers: 50000,
        disbursed: 500,
        approval: 30,
        satisfaction: 99.9,
      };

      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        const easeOut = 1 - Math.pow(1 - progress, 3);

        setCounters({
          customers: Math.floor(targets.customers * easeOut),
          disbursed: Math.floor(targets.disbursed * easeOut),
          approval: Math.floor(targets.approval * easeOut),
          satisfaction: Math.floor(targets.satisfaction * easeOut * 10) / 10,
        });

        if (currentStep >= steps) {
          clearInterval(timer);
          setCounters(targets);
        }
      }, stepDuration);
    };

    // Start counter animation when stats section is visible
    if (isVisible["benefits-stats"]) {
      animateCounters();
    }
  }, [isVisible["benefits-stats"]]);

  // Typing animation
  useEffect(() => {
    if (isVisible["hero-section"] && typingTexts && typingTexts.length > 0) {
      const currentText = typingTexts[typingIndex];
      if (currentText && typingIndex < currentText.length) {
        const timer = setTimeout(() => {
          setTypingText(currentText.substring(0, typingIndex + 1));
          setTypingIndex(typingIndex + 1);
        }, 100);
        return () => clearTimeout(timer);
      } else if (currentText && typingIndex >= currentText.length) {
        // Reset to start typing the next text or loop back
        const timer = setTimeout(() => {
          setTypingIndex(0);
          setTypingText("");
        }, 2000); // Wait 2 seconds before restarting
        return () => clearTimeout(timer);
      }
    }
  }, [typingIndex, isVisible["hero-section"]]);

  // Mouse tracking for magnetic effects
  const handleMouseMove = (e, elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      element.style.setProperty("--mouse-x", `${x * 0.1}px`);
      element.style.setProperty("--mouse-y", `${y * 0.1}px`);
    }
  };

  return (
    <>
      <div className="page_wrapper">
        <section className="about-simple-banner">
          <div className="about-simple-container">
            {/* Left Side - Image */}
            <div className="about-simple-image">
              <img
                src={AboutBannerImage}
                alt="About Udhaar Portal"
                className="about-banner-img"
              />
            </div>

            {/* Right Side - Content */}
            <div className="about-simple-content">
              <h1 className="about-simple-title">
                Empowering Financial Freedom
                <span className="about-simple-subtitle">
                  One Loan at a Time
                </span>
              </h1>
              <p className="about-simple-description">
                We're revolutionizing personal finance in India with instant,
                transparent, and accessible loan solutions. Our mission is to
                bridge the gap between traditional banking and modern financial
                needs.
              </p>
            </div>
          </div>
        </section>

        {/* Eligibility & Documents Section */}
        <section
          id="eligibility-documents-section"
          ref={(el) =>
            (sectionRefs.current["eligibility-documents-section"] = el)
          }
          className="about-eligibility-section"
        >
          <div className="about-eligibility-container">
            <div
              className={`about-eligibility-content about-reveal ${
                isVisible["eligibility-documents-section"]
                  ? "about-revealed"
                  : ""
              }`}
            >
              <div className="about-eligibility-header">
                <h2 className="about-eligibility-title">ABOUT UDHAAR PORTAL</h2>
                <p className="about-eligibility-subtitle">
                  OUR MISSION AND VALUES DRIVING FINANCIAL INNOVATION IN INDIA
                </p>
              </div>

              <div className="about-eligibility-cards">
                {/* Our Mission Card */}
                <div className="about-eligibility-card">
                  <div className="about-eligibility-card-header">
                    <h3>Our Mission</h3>
                  </div>
                  <div className="about-eligibility-card-content">
                    <p className="about-eligibility-card-intro">
                      We are revolutionizing personal finance in India with
                      instant, transparent, and accessible loan solutions that
                      bridge the gap between traditional banking and modern
                      financial needs.
                    </p>
                    <ul className="about-eligibility-card-list">
                      <li className="about-eligibility-card-item">
                        <div className="about-eligibility-check">✓</div>
                        <div className="about-eligibility-text">
                          <strong>Financial Inclusion:</strong> Making loans
                          accessible to every working professional across India.
                        </div>
                      </li>
                      <li className="about-eligibility-card-item">
                        <div className="about-eligibility-check">✓</div>
                        <div className="about-eligibility-text">
                          <strong>Transparent Processes:</strong> Clear terms,
                          no hidden fees, and complete transparency.
                        </div>
                      </li>
                      <li className="about-eligibility-card-item">
                        <div className="about-eligibility-check">✓</div>
                        <div className="about-eligibility-text">
                          <strong>Customer-Centric:</strong> Dedicated support
                          and personalized solutions for every customer.
                        </div>
                      </li>
                      <li className="about-eligibility-card-item">
                        <div className="about-eligibility-check">✓</div>
                        <div className="about-eligibility-text">
                          <strong>Innovation:</strong> Cutting-edge fintech
                          platform with advanced technology.
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Our Values Card */}
                <div className="about-eligibility-card">
                  <div className="about-eligibility-card-header">
                    <h3>Our Values</h3>
                  </div>
                  <div className="about-eligibility-card-content">
                    <p className="about-eligibility-card-intro">
                      As an RBI-registered NBFC, we are committed to empowering
                      millennials with innovative loan options, ensuring fast
                      approvals with customer-friendly terms and complete
                      security.
                    </p>
                    <ul className="about-eligibility-card-list">
                      <li className="about-eligibility-card-item">
                        <div className="about-eligibility-check">✓</div>
                        <div className="about-eligibility-text">
                          <strong>Trust & Security:</strong> Bank-level security
                          for all transactions and data protection.
                        </div>
                      </li>
                      <li className="about-eligibility-card-item">
                        <div className="about-eligibility-check">✓</div>
                        <div className="about-eligibility-text">
                          <strong>Speed & Efficiency:</strong> Quick decisions
                          with advanced technology and instant approvals.
                        </div>
                      </li>
                      <li className="about-eligibility-card-item">
                        <div className="about-eligibility-check">✓</div>
                        <div className="about-eligibility-text">
                          <strong>Regulatory Compliance:</strong> Fully
                          compliant with RBI standards and government norms.
                        </div>
                      </li>
                      <li className="about-eligibility-card-item">
                        <div className="about-eligibility-check">✓</div>
                        <div className="about-eligibility-text">
                          <strong>Reliability:</strong> Your trusted partner for
                          personal loans and financial solutions.
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Powering Instant Loans Section */}
        <section
          id="powering-section"
          ref={(el) => (sectionRefs.current["powering-section"] = el)}
          className="about-powering-section"
        >
          {/* Floating Background Elements - Removed for cleaner design */}

          <div className="about-powering-container">
            <div
              className={`about-powering-content about-reveal ${
                isVisible["powering-section"] ? "about-revealed" : ""
              }`}
            >
              <div className="about-powering-header">
                <h2 className="about-powering-title about-glow">
                  <span className="about-powering-highlight">
                    POWERING INSTANT LOANS
                  </span>
                  <span className="about-powering-subtitle">
                    WITH SCALABLE FINANCIAL SOLUTIONS
                  </span>
                </h2>
                <div className="about-powering-divider"></div>
              </div>

              <div className="about-powering-description">
                <p className="about-powering-intro">
                  At Udhaar Portal, we simplify financial access for individuals
                  through instant approval, secure processing, and seamless
                  operations - purpose-built for modern lending needs.
                </p>

                <p className="about-powering-details">
                  Backed by a strategic partnership with an RBI-registered NBFC
                  and an advanced fintech platform, we deliver a smooth, secure,
                  and efficient digital lending experience. Our mission is to
                  make financial inclusion a reality for every working
                  professional across India, ensuring transparent processes and
                  customer-centric solutions.
                </p>

                <p className="about-powering-mission">
                  Udhaar Portal is your trusted lending facilitation platform
                  for personal loans tailored to salaried individuals. Whether
                  it is an emergency expense, medical emergency or home
                  improvement. Helping you access quick and flexible funding
                  through our NBFC partners.
                </p>
              </div>

              <div className="about-powering-features">
                <div className="about-powering-feature about-card-3d about-magnetic">
                  <div className="about-powering-icon about-glow">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3>Instant Approval</h3>
                  <p>Quick decisions with advanced technology</p>
                </div>

                <div className="about-powering-feature about-card-3d about-magnetic">
                  <div className="about-powering-icon about-glow">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3>Secure Processing</h3>
                  <p>Bank-level security for all transactions</p>
                </div>

                <div className="about-powering-feature about-card-3d about-magnetic">
                  <div className="about-powering-icon about-glow">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7ZM23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3>Customer-Centric</h3>
                  <p>Dedicated support for every customer</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section
          id="how-it-works"
          ref={(el) => (sectionRefs.current["how-it-works"] = el)}
          className="about-how-it-works"
        >
          {/* Floating Background Elements - Removed for cleaner design */}

          <div className="about-how-container">
            <div
              className={`about-how-header about-reveal ${
                isVisible["how-it-works"] ? "about-revealed" : ""
              }`}
            >
              <h2 className="about-how-title about-glow">
                <span className="about-how-highlight">How It Works</span>
              </h2>
              <p className="about-how-subtitle">
                Get your personal loan in just 3 simple steps
              </p>
              <div className="about-how-divider"></div>
            </div>

            <div
              className={`about-how-steps about-reveal-left ${
                isVisible["how-it-works"] ? "about-revealed" : ""
              }`}
            >
              <div className="about-step about-card-3d about-magnetic">
                <div className="about-step-number about-glow">
                  <span>1</span>
                </div>
                <div className="about-step-content">
                  <div className="about-step-icon about-glow">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3 className="about-step-title">Online Application</h3>
                  <p className="about-step-description">
                    Start by filling out our quick and easy loan application
                    form online. You'll need to provide some basic personal
                    information, employment details, and the loan amount you
                    wish to apply for.
                  </p>
                </div>
                <div className="about-step-arrow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <div className="about-step about-card-3d about-magnetic">
                <div className="about-step-number about-glow">
                  <span>2</span>
                </div>
                <div className="about-step-content">
                  <div className="about-step-icon about-glow">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3 className="about-step-title">Instant Verification</h3>
                  <p className="about-step-description">
                    Our advanced technology allows us to verify your documents
                    and creditworthiness immediately. Within minutes, you will
                    receive a preliminary decision on your loan application.
                  </p>
                </div>
                <div className="about-step-arrow about-glow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <div className="about-step about-card-3d about-magnetic">
                <div className="about-step-number about-glow">
                  <span>3</span>
                </div>
                <div className="about-step-content">
                  <div className="about-step-icon about-glow">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 2L2 7L12 12L22 7L12 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 17L12 22L22 17"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 12L12 17L22 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3 className="about-step-title">Approval and Disbursal</h3>
                  <p className="about-step-description">
                    Once your loan is approved, the funds will be transferred
                    directly to your bank account. In most cases, this happens
                    within 24-48 hours of approval, but with us, you can get it
                    in mere 5 minutes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="benefits-section"
          ref={(el) => (sectionRefs.current["benefits-section"] = el)}
          className="about-benefits-section"
        >
          {/* Floating Background Elements - Removed for cleaner design */}

          <div className="about-benefits-container">
            <div
              className={`about-benefits-header about-reveal ${
                isVisible["benefits-section"] ? "about-revealed" : ""
              }`}
            >
              <h2 className="about-benefits-title about-glow">
                <span className="about-benefits-highlight">
                  Why Personal Loans
                </span>
                <span className="about-benefits-subtitle">
                  Are Your Best Financial Choice
                </span>
              </h2>
              <p className="about-benefits-description">
                Discover the advantages of choosing personal loans for your
                financial needs
              </p>
              <div className="about-benefits-divider"></div>
            </div>

            <div
              className={`about-benefits-grid about-reveal-right ${
                isVisible["benefits-section"] ? "about-revealed" : ""
              }`}
            >
              <div className="about-benefit-card about-card-3d about-magnetic">
                <div className="about-benefit-icon about-glow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L2 7L12 12L22 7L12 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 17L12 22L22 17"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 12L12 17L22 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>No Collateral Required</h3>
                <p>
                  Get instant approval without pledging any assets or property
                  as security for your loan.
                </p>
                <div className="about-benefit-highlight">Unsecured</div>
              </div>

              <div className="about-benefit-card about-card-3d about-magnetic">
                <div className="about-benefit-icon about-glow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>Instant Approval</h3>
                <p>
                  Our advanced technology provides loan approval within minutes,
                  not days or weeks.
                </p>
                <div className="about-benefit-highlight">5 Minutes</div>
              </div>

              <div className="about-benefit-card about-card-3d about-magnetic">
                <div className="about-benefit-icon about-glow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>Flexible Usage</h3>
                <p>
                  Use your loan for any purpose - medical emergencies, home
                  improvement, travel, or debt consolidation.
                </p>
                <div className="about-benefit-highlight">Multi-Purpose</div>
              </div>

              <div className="about-benefit-card about-card-3d about-magnetic">
                <div className="about-benefit-icon about-glow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>Competitive Interest Rates</h3>
                <p>
                  Enjoy attractive interest rates starting from 12% p.a. with
                  transparent pricing and no hidden charges.
                </p>
                <div className="about-benefit-highlight">12% p.a.</div>
              </div>

              <div className="about-benefit-card about-card-3d about-magnetic">
                <div className="about-benefit-icon about-glow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7ZM23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>24/7 Customer Support</h3>
                <p>
                  Get round-the-clock assistance from our dedicated customer
                  support team for all your queries.
                </p>
                <div className="about-benefit-highlight">Always Available</div>
              </div>

              <div className="about-benefit-card about-card-3d about-magnetic">
                <div className="about-benefit-icon about-glow">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 12L10 14L16 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>RBI Registered & Secure</h3>
                <p>
                  Bank with confidence knowing we're RBI-registered NBFC with
                  bank-level security for all transactions.
                </p>
                <div className="about-benefit-highlight">100% Secure</div>
              </div>
            </div>

            <div
              id="benefits-stats"
              ref={(el) => (sectionRefs.current["benefits-stats"] = el)}
              className={`about-benefits-stats about-reveal ${
                isVisible["benefits-stats"] ? "about-revealed" : ""
              }`}
            >
              <div className="about-stat-item about-card-3d about-magnetic about-mobile-stat-item">
                <div className="about-stat-number about-counter about-glow about-mobile-stat-number">
                  {counters.customers.toLocaleString()}K+
                </div>
                <div className="about-stat-label about-mobile-stat-label">
                  Happy Customers
                </div>
              </div>
              <div className="about-stat-item about-card-3d about-magnetic about-mobile-stat-item">
                <div className="about-stat-number about-counter about-glow about-mobile-stat-number">
                  ₹{counters.disbursed}Cr+
                </div>
                <div className="about-stat-label about-mobile-stat-label">
                  Loans Disbursed
                </div>
              </div>
              <div className="about-stat-item about-card-3d about-magnetic about-mobile-stat-item">
                <div className="about-stat-number about-counter about-glow about-mobile-stat-number">
                  {counters.approval}min
                </div>
                <div className="about-stat-label about-mobile-stat-label">
                  Average Approval
                </div>
              </div>
              <div className="about-stat-item about-card-3d about-magnetic about-mobile-stat-item">
                <div className="about-stat-number about-counter about-glow about-mobile-stat-number">
                  {counters.satisfaction}%
                </div>
                <div className="about-stat-label about-mobile-stat-label">
                  Customer Satisfaction
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Simple Banner Section */}
      </div>
    </>
  );
};

export default About;
